import type { HttpErrorResponse } from '@angular/common/http';

import type { DynamicFormConfig } from '@core/ask-hr-dynamic-forms-types';
import type {
  AskHrTicket,
  CreateAskTicket,
  MoveAskTicket,
  UpdateAskHrTicket,
} from '@core/ask-hr-tickets-types';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  type PickActions,
} from '@core/ngrx-legacy';

const idSelector = (template: { id: number }) => template.id;
const { selectors, reducer, actions, namespace } = createEntityCollectionStore<AskHrTicket>()(
  'AskHrTickets',
  {
    idSelector,
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);
      return {
        createOnePessimistic: (
          state,
          payload: { data: CreateAskTicket<DynamicFormConfig>; correlationId: string },
        ) => adapter.markAsPending(state, { entities: [payload.correlationId], scope: null }),

        fetchOne: (state, payload: { id: AskHrTicket['id'] }) =>
          defaultActions.fetchOne(state, payload),

        updateOnePessimistic: (state, payload: { data: UpdateAskHrTicket; id: number }) =>
          adapter.markAsPending(state, { entities: [payload.id], scope: null }),

        move: (state, payload: { data: MoveAskTicket; id: number }) =>
          adapter.markAsPending(state, { entities: [payload.id], scope: null }),
        moveSuccess: (state, payload: { data: AskHrTicket }) =>
          defaultActions.updateOnePessimisticSuccess(state, payload),
        moveFailed: (state, payload: { error: HttpErrorResponse; data: { id: number } }) =>
          defaultActions.updateOnePessimisticFailed(state, payload),

        cancel: (state, payload: { id: AskHrTicket['id'] }) =>
          adapter.markAsPending(state, { entities: [payload.id], scope: null }),
        cancelSuccess: (state, payload: { data: AskHrTicket }) =>
          defaultActions.removeOnePessimisticSuccess(state, payload),
        cancelFailed: (state, payload: { error: HttpErrorResponse; data: AskHrTicket['id'] }) =>
          defaultActions.removeOnePessimisticFailed(state, payload),
      };
    },
  },
);

const AskTicketsActions: PickActions<
  typeof actions,
  'createOnePessimistic' | 'fetchOne' | 'updateOnePessimistic' | 'move' | 'cancel'
> = actions;

export {
  selectors as AskTicketsSelectors,
  reducer as AskTicketsReducer,
  AskTicketsActions,
  namespace as AskTicketsNamespace,
};
